import { useState } from 'react'
import type { ClubProjectState, GameState, SeasonObjective, StakeholderGroup, StakeholderState, StandingPolicies } from '../game/types'
import { CLUB_PROJECT_CATALOGUE, selectClubProject, lockAnnualClubPlan, updateStandingPolicies, selectClubProjectBranch } from '../game/clubStrategy'
import { ContractsTab, FacilitiesTab, FinanceTab, StaffMarketTab } from './ClubManagementTabs'

interface ClubScreenProps {
  state: GameState
  onBack: () => void
  onAction: (action: (state: GameState) => GameState) => void
}

const STAKEHOLDER_LABELS: Record<StakeholderGroup, { label: string; short: string }> = {
  board: { label: 'Board', short: 'BRD' },
  supporters: { label: 'Supporters', short: 'SUP' },
  seniorPlayers: { label: 'Senior Players', short: 'SEN' },
  youngPlayers: { label: 'Young Players', short: 'YNG' },
  sponsors: { label: 'Sponsors', short: 'SPN' },
}

const STAKEHOLDER_INFLUENCE: Record<StakeholderGroup, number> = {
  board: 90, supporters: 75, seniorPlayers: 80, youngPlayers: 65, sponsors: 60,
}

function StakeholderRow({ group, state }: { group: StakeholderGroup; state: StakeholderState }) {
  const { label, short } = STAKEHOLDER_LABELS[group]
  const influence = STAKEHOLDER_INFLUENCE[group]
  const trendIcon = state.trend === 'rising' ? '▲' : state.trend === 'falling' ? '▼' : '●'
  const trendClass = state.trend === 'rising' ? 'rising' : state.trend === 'falling' ? 'falling' : ''
  return (
    <div className="stakeholder-row" data-tooltip={state.evidence}>
      <span className="stakeholder-name" data-tooltip={short}>{label}</span>
      <div className="stakeholder-bar"><div className="stakeholder-fill" style={{ width: `${state.confidence}%` }} /></div>
      <span className={`stakeholder-value ${trendClass}`}>{state.confidence}</span>
      <span className={`stakeholder-trend ${trendClass}`}>{trendIcon}</span>
      <span className="stakeholder-influence">I:{influence}</span>
    </div>
  )
}

function ObjectiveRow({ objective }: { objective: SeasonObjective }) {
  const { progress, type, target, status, title } = objective
  const isLadder = type === 'ladder-position'
  const current = isLadder ? (progress.current > 0 ? progress.current : '—') : progress.current
  const pct = isLadder ? 0 : target.value > 0 ? Math.min(100, Math.round((progress.current / target.value) * 100)) : 0
  const statusIcon = status === 'achieved' ? '✓' : status === 'missed' ? '✗' : status === 'in-progress' ? '◐' : '○'
  return (
    <div className="objective-row">
      <div className="objective-info">
        <strong>{title}</strong>
        <span className="objective-meta">{current} / {target.value} ({pct}%)</span>
      </div>
      <div className="objective-progress"><div className="progress-track"><div className="progress-fill" style={{ width: `${pct}%` }} /></div></div>
      <span className={`objective-status ${status === 'achieved' ? 'achieved' : status === 'missed' ? 'missed' : ''}`}>{statusIcon}</span>
    </div>
  )
}

function ProjectSection({ project, onAction, annualPlanLocked }: { project: ClubProjectState; onAction: ClubScreenProps['onAction']; annualPlanLocked: boolean }) {
  const { selectedId, selectedSeason, status, stage, progress, branchReady, availableBranches, selectedBranchId, branchSelectionSeason } = project
  const definition = selectedId ? CLUB_PROJECT_CATALOGUE.find(p => p.id === selectedId) : null
  const pct = progress.target > 0 ? Math.min(100, Math.round((progress.current / progress.target) * 100)) : 0
  const branchLocked = branchSelectionSeason !== null

  const resetProject = () => onAction(s => ({
    ...s,
    clubStrategy: {
      ...s.clubStrategy,
      project: {
        ...s.clubStrategy.project,
        selectedId: null,
        selectedSeason: null,
        status: 'unselected',
        stage: 'foundation',
        progress: { metric: null, current: 0, target: 0, baseline: 0 },
        selectionBaseline: 0,
        branchReady: false,
        availableBranches: [],
        selectedBranchId: null,
        branchSelectionSeason: null,
        promise: '',
        tradeOff: '',
        abandonmentCost: 0,
      },
    },
  }))

  return (
    <section className="panel project-section">
      <div className="section-heading">
        <h2>Club Project</h2>
        {selectedId && !annualPlanLocked && (
          <button type="button" className="change-link" onClick={resetProject}>Change</button>
        )}
      </div>

      {!selectedId ? (
        <div className="project-selector">
          <p className="selector-hint">Choose one direction for this season. Shapes expectations, staff recommendations and weekly trade-offs.</p>
          <div className="project-grid">
            {CLUB_PROJECT_CATALOGUE.map(proj => (
              <button
                key={proj.id}
                type="button"
                className="project-option"
                onClick={() => onAction(s => selectClubProject(s, proj.id))}
                disabled={annualPlanLocked}
              >
                <strong>{proj.title}</strong>
                <span className="metric">{proj.metric} target: {proj.target}</span>
                <p className="promise">{proj.promise}</p>
                <p className="tradeoff">{proj.tradeOff}</p>
              </button>
            ))}
          </div>
        </div>
      ) : (
        <>
          <div className="project-header">
            <div>
              <strong>{definition?.title}</strong>
              <span className="project-meta">Season {selectedSeason} · {status === 'complete' ? 'Complete' : stage === 'established' ? 'Established' : 'Foundation'}</span>
            </div>
            <div className="project-progress-bar">
              <div className="progress-track"><div className={`progress-fill ${status === 'complete' ? 'complete' : ''}`} style={{ width: `${pct}%` }} /></div>
              <span>{progress.current} / {progress.target} ({pct}%)</span>
            </div>
          </div>

          {!branchLocked && branchReady && !selectedBranchId && (
            <div className="branch-selector">
              <h4>Branch ready — choose a direction</h4>
              <div className="branch-options">
                {availableBranches.map(branch => (
                  <button
                    key={branch.id}
                    type="button"
                    className="branch-option"
                    onClick={() => onAction(s => selectClubProjectBranch(s, branch.id))}
                    disabled={annualPlanLocked}
                  >
                    <strong>{branch.title}</strong>
                    <p>{branch.promise}</p>
                    <span className="tradeoff">{branch.tradeOff}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {selectedBranchId && (
            <div className="branch-selected"><strong>Branch locked for Season {branchSelectionSeason}:</strong> {availableBranches.find(b => b.id === selectedBranchId)?.title || definition?.branches.find(b => b.id === selectedBranchId)?.title}</div>
          )}

          {!branchLocked && status !== 'complete' && !branchReady && (
            <p className="progress-hint">Progress {progress.current}/{progress.target} — reach {Math.ceil(progress.target * 0.5)} to unlock branch.</p>
          )}

          {status === 'complete' && <p className="complete-note">✓ Project complete — benefits carry forward.</p>}
        </>
      )}
    </section>
  )
}

function PoliciesSection({ policies, annualPlanLocked, onAction }: { policies: StandingPolicies; annualPlanLocked: boolean; onAction: ClubScreenProps['onAction'] }) {
  const POLICY_OPTIONS: Record<keyof StandingPolicies, { value: string; label: string; description: string }[]> = {
    selectionPreference: [
      { value: 'merit', label: 'Merit', description: 'Best players selected regardless of age or development status.' },
      { value: 'youth-opportunity', label: 'Youth Opportunity', description: 'Young players get the nod when selection is close.' },
      { value: 'continuity', label: 'Continuity', description: 'Protect incumbents unless form drops sharply.' },
    ],
    welfareRisk: [
      { value: 'cautious', label: 'Cautious', description: 'Protect players from early returns; accept lower short-term output.' },
      { value: 'balanced', label: 'Balanced', description: 'Standard approach to fitness, fatigue and injury risk.' },
      { value: 'aggressive', label: 'Aggressive', description: 'Push returns for weekly impact; higher re-injury risk.' },
    ],
    researchPosture: [
      { value: 'targeted', label: 'Targeted', description: 'Spend only on the current opponent and priority areas.' },
      { value: 'balanced', label: 'Balanced', description: 'Standard weekly research spread.' },
      { value: 'broad', label: 'Broad', description: 'Invest heavily in scouting and long-term knowledge.' },
    ],
    institutionalPosture: [
      { value: 'protect', label: 'Protect Capital', description: 'Preserve institutional capital unless intervention is essential.' },
      { value: 'balanced', label: 'Balanced', description: 'Standard approach to institutional spending.' },
      { value: 'spend-to-win', label: 'Spend to Win', description: 'Deploy institutional capital to chase immediate results.' },
    ],
  }

  if (annualPlanLocked) {
    return (
      <section className="panel policies locked">
        <div className="section-heading"><h2>Standing Policies</h2></div>
        <div className="policies-locked-grid">
          {(Object.keys(POLICY_OPTIONS) as Array<keyof StandingPolicies>).map(key => (
            <div key={key} className="policy-locked"><strong>{key.replace(/([A-Z])/g, ' $1')}</strong><span>{policies[key].replace(/-/g, ' ')}</span></div>
          ))}
        </div>
      </section>
    )
  }

  return (
    <section className="panel policies">
      <div className="section-heading"><h2>Standing Policies</h2></div>
      <p className="policies-hint">Alter staff defaults each week. Change anytime before locking.</p>
      {(Object.keys(POLICY_OPTIONS) as Array<keyof StandingPolicies>).map(key => (
        <div key={key} className="policy-row">
          <label className="policy-label">{key.replace(/([A-Z])/g, ' $1')}</label>
          <div className="policy-options">
            {POLICY_OPTIONS[key].map(opt => (
              <label key={opt.value} className={`policy-option ${policies[key] === opt.value ? 'selected' : ''}`}>
                <input type="radio" name={`policy-${key}`} checked={policies[key] === opt.value} onChange={() => onAction(s => updateStandingPolicies(s, { [key]: opt.value }))} />
                <div><span>{opt.label}</span><small>{opt.description}</small></div>
              </label>
            ))}
          </div>
        </div>
      ))}
    </section>
  )
}

function LockSection({ annualPlan, project, policies, onAction }: { annualPlan: { season: number; locked: boolean; confirmed: boolean }; project: ClubProjectState; policies: StandingPolicies; onAction: ClubScreenProps['onAction'] }) {
  const canLock = project.selectedId && CLUB_PROJECT_CATALOGUE.some(p => p.id === project.selectedId)
  const policiesValid = ['merit', 'youth-opportunity', 'continuity'].includes(policies.selectionPreference)
    && ['cautious', 'balanced', 'aggressive'].includes(policies.welfareRisk)
    && ['targeted', 'balanced', 'broad'].includes(policies.researchPosture)
    && ['protect', 'balanced', 'spend-to-win'].includes(policies.institutionalPosture)

  if (annualPlan.locked) {
    return (
      <section className="panel lock-section locked">
        <div className="section-heading"><h2>Season {annualPlan.season} Plan — Locked</h2></div>
        <div className="lock-summary">
          <div><strong>Project:</strong> {project.selectedId ? CLUB_PROJECT_CATALOGUE.find(p => p.id === project.selectedId)?.title : 'None'}</div>
          <div><strong>Branch:</strong> {project.selectedBranchId ? 'Selected' : 'Not chosen'}</div>
          <div><strong>Policies:</strong> All 4 set</div>
        </div>
        <p className="locked-note">Fixed until next pre-season.</p>
      </section>
    )
  }

  return (
    <section className="panel lock-section">
      <div className="section-heading"><h2>Lock Season {annualPlan.season} Plan</h2></div>
      <div className="lock-requirements">
        <div className={`req ${canLock ? 'met' : 'unmet'}`}><span>{canLock ? '✓' : '✗'}</span> Project selected</div>
        <div className={`req ${policiesValid ? 'met' : 'unmet'}`}><span>{policiesValid ? '✓' : '✗'}</span> All 4 policies set</div>
      </div>
      <button
        type="button"
        className={`primary-action ${canLock && policiesValid ? '' : 'disabled'}`}
        onClick={() => onAction(s => lockAnnualClubPlan(s))}
        disabled={!canLock || !policiesValid}
      >
        {canLock && policiesValid ? `Lock Season ${annualPlan.season} Plan` : 'Complete requirements above'}
      </button>
    </section>
  )
}

export function ClubScreen({ state, onBack, onAction }: ClubScreenProps) {
  const [tab, setTab] = useState<'strategy' | 'contracts' | 'facilities' | 'staff' | 'finance'>('strategy')
  const { clubStrategy, phase } = state
  const { stakeholders, objective, project, policies, annualPlan } = clubStrategy
  const preseason = phase === 'preseason'
  const annualPlanLocked = annualPlan.locked

  return (
    <div className="screen club-screen">
      <button className="back-link" type="button" onClick={onBack}>Back to Week</button>
      <div className="page-title">
        <div>
          <span className="eyebrow">{preseason ? 'Pre-season planning' : 'Club strategy'}</span>
          <h1>Club</h1>
          <p>{annualPlanLocked ? `Season ${annualPlan.season} plan locked` : 'Set your project, policies and lock the plan.'}</p>
        </div>
        <span className={`status-pill ${annualPlanLocked ? 'locked' : 'unlocked'}`}>{annualPlanLocked ? `Season ${annualPlan.season} locked` : 'Plan unlocked'}</span>
      </div>

      <nav className="club-subnav" aria-label="Club management sections">
        {([['strategy', 'Strategy'], ['contracts', 'Contracts'], ['facilities', 'Facilities'], ['staff', 'Staff Market'], ['finance', 'Sponsors & Finance']] as const).map(([id, label]) => (
          <button type="button" key={id} className={tab === id ? 'active' : ''} aria-current={tab === id ? 'page' : undefined} onClick={() => setTab(id)}>{label}</button>
        ))}
      </nav>

      <div className="club-tab" hidden={tab !== 'strategy'}>
      <section className="panel stakeholders">
        <div className="section-heading"><h2>Stakeholder Confidence</h2></div>
        <div className="stakeholder-list">
          {(Object.keys(stakeholders) as StakeholderGroup[]).map(group => (
            <StakeholderRow key={group} group={group} state={stakeholders[group]} />
          ))}
        </div>
      </section>

      <ObjectiveRow objective={objective} />

      <ProjectSection project={project} onAction={onAction} annualPlanLocked={annualPlanLocked} />

      <PoliciesSection policies={policies} annualPlanLocked={annualPlanLocked} onAction={onAction} />

      <LockSection annualPlan={annualPlan} project={project} policies={policies} onAction={onAction} />
      </div>

      {tab === 'contracts' && <div className="club-tab"><ContractsTab state={state} onAction={onAction} /></div>}
      {tab === 'facilities' && <div className="club-tab"><FacilitiesTab state={state} onAction={onAction} /></div>}
      {tab === 'staff' && <div className="club-tab"><StaffMarketTab state={state} onAction={onAction} /></div>}
      {tab === 'finance' && <div className="club-tab"><FinanceTab state={state} /></div>}

      <div className="sticky-actions"><button className="primary-action" type="button" onClick={onBack}>Return to Week</button></div>
    </div>
  )
}